import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { USER_KEY, extractError, getStoredToken, getStoredUser, setStoredAuth, clearStoredAuth } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getStoredUser());
  const [token, setToken] = useState(() => getStoredToken());
  const [initializing, setInitializing] = useState(true);

  const logout = useCallback(() => {
    clearStoredAuth();
    setToken(null);
    setUser(null);
  }, []);

  // Validate existing session on mount
  useEffect(() => {
    let active = true;
    async function validate() {
      if (!token) {
        setInitializing(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        if (active && data) {
          setUser(data);
          // Refresh cached user in whichever store currently holds the token
          const store = localStorage.getItem(USER_KEY) !== null ? localStorage : sessionStorage;
          store.setItem(USER_KEY, JSON.stringify(data));
        }
      } catch (e) {
        if (active) logout();
      } finally {
        if (active) setInitializing(false);
      }
    }
    validate();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global 401 handler
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener("hh-unauthorized", handler);
    return () => window.removeEventListener("hh-unauthorized", handler);
  }, [logout]);

  const login = useCallback(async (email, password, remember = true) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const accessToken = data.access_token;
      const loggedUser = data.user;
      setStoredAuth(accessToken, loggedUser, remember);
      setToken(accessToken);
      setUser(loggedUser);
      return { ok: true, user: loggedUser };
    } catch (e) {
      return { ok: false, error: extractError(e, "Incorrect email or password.") };
    }
  }, []);

  const value = useMemo(
    () => ({ user, token, isAuthenticated: !!token, initializing, login, logout }),
    [user, token, initializing, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
